'use client'

import { createContext, PropsWithChildren, ReactNode, useContext, useMemo } from 'react'
import { logger } from '@/utils/logger'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { AdmissionType } from '@/types/general.types'

type EventContextT = {
  event: Nullable<Transformed.Event>
  eventVenue: Nullable<Transformed.Venue>
  relatedEvents: Nullable<readonly Transformed.EventForCard[]>
  associatedEvents: Nullable<Transformed.AssociatedEvents>
  eventsPricing: Nullable<readonly Transformed.PriceDisplayByEvent[]>
  eventIds: Nullable<DataAccessInput.EventIdsInput>
}

type EventProviderProps = {
  event: Transformed.Event | Transformed.EventWithTimeslotsAndMeta
  eventVenue: Transformed.Venue
  relatedEvents: readonly Transformed.EventForCard[]
  associatedEvents: Transformed.AssociatedEvents
  eventsPricing: readonly Transformed.PriceDisplayByEvent[]
}

const { warn } = logger()

const EventContext = createContext<EventContextT>({
  event: null,
  eventVenue: null,
  relatedEvents: null,
  associatedEvents: null,
  eventsPricing: null,
  eventIds: null,
})

/**
 * Provides a context for managing and accessing event-related data.
 *
 * This component wraps its children with an `EventContext` that contains
 * the event details, related events, and pricing information. It uses
 * React's `useRef` and `useMemo` to store and memoize these values for
 * consistent reference.
 *
 * @param children - The child components to be rendered within the provider.
 * @param event - The primary event data, expected to be transformed and nullable.
 * @param associatedEvents
 * @param relatedEvents - A list of related events, expected to be transformed,
 *                        readonly, and nullable.
 * @param eventsPricing - Pricing details for the events, expected to be an array
 *                        of transformed price displays, readonly, and nullable.
 *
 * @returns A ReactNode wrapping the children within the context provider.
 */
export const EventProvider = ({
  children,
  event,
  eventVenue,
  associatedEvents,
  relatedEvents,
  eventsPricing,
}: PropsWithChildren<EventProviderProps>): ReactNode => {
  const eventIds = useMemo(() => ({
    timedTicket: event?.id,
    addOn: associatedEvents.addOns?.id || null,
    dayPass: associatedEvents.dayPass?.id || null,
  }), [event, associatedEvents])

  const value = useMemo(() => ({
    event,
    eventVenue,
    associatedEvents,
    eventsPricing,
    relatedEvents,
    eventIds,
  }), [associatedEvents, event, eventVenue, eventIds, eventsPricing, relatedEvents])

  return (
    <EventContext value={value}>
      {children}
    </EventContext>
  )
}

export function useEvent<T = Transformed.Event,>(): T {
  const eventContext = useContext(EventContext)

  if (!eventContext) {
    throw new Error('Event context is not defined')
  }

  if (!eventContext.event) {
    throw new Error('Event is not defined')
  }

  return eventContext.event as T
}

export function useRelatedEvents<T = Transformed.EventForCard>(): readonly T[] {
  const eventContext = useContext(EventContext)

  if (!eventContext) {
    throw new Error('Event context is not defined')
  }

  if (!eventContext.relatedEvents) {
    return []
  }

  return eventContext.relatedEvents as readonly T[]
}

export const useAssociatedEvents = (): Transformed.AssociatedEvents => {
  const eventContext = useContext(EventContext)

  if (!eventContext) {
    throw new Error('Event context is not defined')
  }

  if (!eventContext.associatedEvents) {
    warn('Associated events are not defined')
    return {
      dayPass: null,
      addOns: null,
    }
  }

  return eventContext.associatedEvents
}

export const useEventsPricing = (): readonly Transformed.PriceDisplayByEvent[] => {
  const eventContext = useContext(EventContext)

  if (!eventContext) {
    throw new Error('Event context is not defined')
  }

  if (!eventContext.eventsPricing) {
    throw new Error('Events pricing is not defined')
  }

  return eventContext.eventsPricing
}

export const useEventIds = (): DataAccessInput.EventIdsInput => {
  const eventContext = useContext(EventContext)

  if (!eventContext.eventIds) {
    throw new Error('Event IDs are not defined')
  }

  return eventContext.eventIds
}

/**
 * The event the current flow is buying from, derived from `admissionType`.
 *
 * This used to be a `selectedEventId` field in the store that had to be written in lockstep
 * with `admissionType`. Any reset that touched one and not the other left the two disagreeing
 * (see docs/day-pass-pricing-bug.md). Deriving it means they cannot drift.
 */
export const useSelectedEventId = (): Nullable<string> => {
  const admissionType = useOurStore((state) => state.admissionType)
  const { timedTicket, dayPass } = useEventIds()

  // A seller without a day pass can't be in the day pass flow, but fall back rather than
  // hand callers a null event id.
  return admissionType === AdmissionType.DayPass ?
    dayPass ?? timedTicket
    : timedTicket
}

export const useEventVenue = (): Transformed.Venue => {
  const eventContext = useContext(EventContext)

  if (!eventContext) {
    throw new Error('Event context is not defined')
  }

  if (!eventContext.eventVenue) {
    throw new Error('Event venue is not defined')
  }

  return eventContext.eventVenue
}
